/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Yakov Gushchin
 * @version 1.20.1
 * @since 1.14.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '../../reducers/rootReducer';
import { IWizardChangeLockValidation } from './types/wizardChangeLockTypes';
import { initWizardChangeLockState } from './wizardChangeLockConstant';
import {
    IWizardChangeLockLockDateParameters,
    IWizardChangeLockOrderResult,
    IWizardChangeLockReducer,
} from './wizardChangeLockReducer';

export const makeSelectWizardChangeLockState = (state: IRootReducer): IWizardChangeLockReducer =>
    state.wizardChangeLock || initWizardChangeLockState;

export const makeSelectWizardChangeLockIsOnlineCheckProceeded = ():
    OutputSelector<IRootReducer, boolean,
        (state: IWizardChangeLockReducer) => boolean> =>
    createSelector(makeSelectWizardChangeLockState, state => state.isOnlineCheckProceeded);

export const makeSelectWizardChangeLockIsUnlockSubscribers = ():
    OutputSelector<IRootReducer, boolean,
        (state: IWizardChangeLockReducer) => boolean> =>
    createSelector(makeSelectWizardChangeLockState, state => state.isUnlockSubscribers);

export const makeSelectWizardChangeLockValidation = ():
    OutputSelector<IRootReducer, IWizardChangeLockValidation,
        (state: IWizardChangeLockReducer) => IWizardChangeLockValidation> =>
    createSelector(makeSelectWizardChangeLockState, state => state.validation);

export const makeSelectWizardChangeLockLockParameters = ():
    OutputSelector<IRootReducer, IWizardChangeLockLockDateParameters,
        (state: IWizardChangeLockReducer) => IWizardChangeLockLockDateParameters> =>
    createSelector(makeSelectWizardChangeLockState, state => state.lockParameters);

export const makeSelectWizardChangeLockMaxLockingPeriod = ():
    OutputSelector<IRootReducer, number,
        (state: IWizardChangeLockReducer) => number> =>
    createSelector(makeSelectWizardChangeLockState, state => state.maxLockingPeriod);

export const makeSelectWizardChangeLockDateFrom = ():
    OutputSelector<IRootReducer, string,
        (state: IWizardChangeLockReducer) => string> =>
    createSelector(makeSelectWizardChangeLockState, state => state.dateFrom);

export const makeSelectWizardChangeLockDateTo = ():
    OutputSelector<IRootReducer, string,
        (state: IWizardChangeLockReducer) => string> =>
    createSelector(makeSelectWizardChangeLockState, state => state.dateTo);

export const makeSelectWizardChangeLockOrderCheckResult = ():
    OutputSelector<IRootReducer, IWizardChangeLockOrderResult,
        (state: IWizardChangeLockReducer) => IWizardChangeLockOrderResult> =>
    createSelector(makeSelectWizardChangeLockState, state => state.orderCheckResult);

export const makeSelectWizardChangeLockSubscriberIds = ():
    OutputSelector<IRootReducer, number[],
        (state: IWizardChangeLockReducer) => number[]> =>
    createSelector(makeSelectWizardChangeLockState, state => state.subscriberIds);

export const makeSelectWizardChangeLockRemovedSubscribers = ():
    OutputSelector<IRootReducer, number[],
        (state: IWizardChangeLockReducer) => number[]> =>
    createSelector(makeSelectWizardChangeLockState, state => state.removedSubscribers);
